import React from "react";
import {
    Box,
    Typography,
    Chip,
    Button,
    Select,
    MenuItem,
    FormControl,
    Collapse,
    Divider,
} from "@mui/material";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AppRegistrationOutlinedIcon from "@mui/icons-material/AppRegistrationOutlined";
import ContactMailOutlinedIcon from "@mui/icons-material/ContactMailOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { useVendorLeadDetailContext } from "contexts/VendorLeadContext";
import { useUpdateLeadOnboardingMutation } from "store/services/vendorLeadApi";

const CHECKLIST_ITEMS = [
    {
        field: "vatPan",
        label: "VAT / PAN",
        description: "Upload valid VAT or PAN registration certificate for tax compliance verification.",
        icon: ReceiptLongOutlinedIcon,
    },
    {
        field: "businessRegistration",
        label: "Business Registration",
        description: "Provide official business registration document issued by the relevant authority.",
        icon: AppRegistrationOutlinedIcon,
    },
    {
        field: "financeContactDoc",
        label: "Finance Contact Document",
        description: "Share finance team contact details and supporting documentation.",
        icon: ContactMailOutlinedIcon,
    },
    {
        field: "slaDoc",
        label: "SLA Document",
        description: "Review and sign the Service Level Agreement outlining operational commitments.",
        icon: HandshakeOutlinedIcon,
    },
    {
        field: "pickupLocationMapping",
        label: "Pickup Location Mapping",
        description: "Map all active pickup locations and verify addresses in the system.",
        icon: FmdGoodOutlinedIcon,
    },
    {
        field: "destinationPreferences",
        label: "Destination Preferences",
        description: "Configure preferred delivery destination zones and restricted areas.",
        icon: TuneOutlinedIcon,
    },
    {
        field: "servicePreferences",
        label: "Service Preferences",
        description: "Set preferred service types, delivery modes, and scheduling preferences.",
        icon: LocalShippingOutlinedIcon,
    },
    {
        field: "portalTraining",
        label: "Portal Training",
        description: "Complete portal walkthrough and training session with the onboarding team.",
        icon: SchoolOutlinedIcon,
    },
];

const STATUS_CONFIG = {
    open: {
        label: "Open",
        color: "#4a9eff",
        bg: "rgba(74,158,255,0.12)",
        border: "rgba(74,158,255,0.35)",
        cardBorder: "rgba(74,158,255,0.25)",
        cardBg: "rgba(74,158,255,0.04)",
        icon: <RadioButtonUncheckedIcon sx={{ fontSize: 16 }} />,
    },
    in_progress: {
        label: "In Progress",
        color: "#ff9a3c",
        bg: "rgba(255,154,60,0.12)",
        border: "rgba(255,154,60,0.35)",
        cardBorder: "rgba(255,154,60,0.25)",
        cardBg: "rgba(255,154,60,0.04)",
        icon: <HourglassEmptyOutlinedIcon sx={{ fontSize: 16 }} />,
    },
    completed: {
        label: "Completed",
        color: "#4caf78",
        bg: "rgba(76,175,120,0.12)",
        border: "rgba(76,175,120,0.35)",
        cardBorder: "rgba(76,175,120,0.25)",
        cardBg: "rgba(76,175,120,0.04)",
        icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />,
    },
};

const StatusChip = ({ status }) => {
    const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.open;
    return (
        <Chip
            icon={React.cloneElement(config.icon, { style: { color: config.color } })}
            label={config.label}
            size="small"
            sx={{
                fontWeight: 700,
                fontSize: "0.72rem",
                backgroundColor: config.bg,
                color: config.color,
                border: "1px solid",
                borderColor: config.border,
                "& .MuiChip-icon": { color: config.color },
            }}
        />
    );
};

const ChecklistCard = ({ item, status, onStatusChange, saving }) => {
    const [dropdownOpen, setDropdownOpen] = React.useState(false);
    const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.open;
    const IconComponent = item.icon;

    return (
        <Box
            sx={{
                border: "1px solid",
                borderColor: config.cardBorder,
                borderRadius: 2,
                backgroundColor: config.cardBg,
                transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                overflow: "hidden",
                "&:hover": {
                    boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                },
            }}
        >
            <Box px={2.5} py={2}>
                {/* Header row */}
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Box display="flex" alignItems="center" gap={1.2}>
                        <IconComponent sx={{ color: config.color, fontSize: 20, mt: 0.1 }} />
                        <Box>
                            <Typography variant="subtitle2" fontWeight={700} lineHeight={1.3}>
                                {item.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                                {item.description}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Right: status badge + Add Update */}
                    <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
                        <StatusChip status={status} />
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setDropdownOpen((prev) => !prev)}
                            sx={{
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                textTransform: "none",
                                whiteSpace: "nowrap",
                                borderColor: "rgba(255,255,255,0.2)",
                                color: "text.primary",
                                "&:hover": { borderColor: config.color, color: config.color },
                            }}
                        >
                            Add Update
                        </Button>
                    </Box>
                </Box>

                {/* Inline dropdown — right aligned below Add Update */}
                <Collapse in={dropdownOpen}>
                    <Divider sx={{ mt: 2, mb: 1.5 }} />
                    <Box display="flex" justifyContent="flex-end">
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                Update Status:
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <Select
                                    value={status}
                                    onChange={(e) => {
                                        onStatusChange(e.target.value);
                                        setDropdownOpen(false);
                                    }}
                                    disabled={saving}
                                    sx={{ fontSize: "0.85rem" }}
                                >
                                    <MenuItem value="open">
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: STATUS_CONFIG.open.color }} />
                                            Open
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="in_progress">
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <HourglassEmptyOutlinedIcon sx={{ fontSize: 16, color: STATUS_CONFIG.in_progress.color }} />
                                            In Progress
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="completed">
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: STATUS_CONFIG.completed.color }} />
                                            Completed
                                        </Box>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                </Collapse>
            </Box>
        </Box>
    );
};

const VendorLeadOnboardingTab = () => {
    const { id } = useParams();
    const { vendorLeadDetail } = useVendorLeadDetailContext();
    const checklist = vendorLeadDetail?.onboardingChecklist ?? {};

    const [localChecklist, setLocalChecklist] = React.useState({});
    const [updateOnboarding, { isLoading: saving }] = useUpdateLeadOnboardingMutation();

    React.useEffect(() => {
        if (vendorLeadDetail?.onboardingChecklist) {
            const initial = {};
            CHECKLIST_ITEMS.forEach(({ field }) => {
                initial[field] = checklist[field] ?? "open";
            });
            setLocalChecklist(initial);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorLeadDetail]);

    const handleStatusChange = async (field, newStatus) => {
        const prev = { ...localChecklist };
        const updated = { ...localChecklist, [field]: newStatus };
        setLocalChecklist(updated);
        try {
            await updateOnboarding({ id, ...updated }).unwrap();
            toast.success("Status updated");
        } catch (err) {
            setLocalChecklist(prev);
            toast.error(err?.data?.message ?? "Could not update status");
        }
    };

    const completedCount = CHECKLIST_ITEMS.filter(
        ({ field }) => localChecklist[field] === "completed"
    ).length;
    const completionPercent = Math.round((completedCount / CHECKLIST_ITEMS.length) * 100);
    const isComplete = completedCount === CHECKLIST_ITEMS.length;

    return (
        <Box padding={3}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2" color="text.secondary">
                    Overall Progress — {completedCount} of {CHECKLIST_ITEMS.length} completed
                </Typography>
                <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color={isComplete ? "#4caf78" : "text.primary"}
                >
                    {completionPercent}%
                </Typography>
            </Box>
            <Box sx={{ height: 6, borderRadius: 1, backgroundColor: "rgba(255,255,255,0.1)", mb: 3, overflow: "hidden" }}>
                <Box
                    sx={{
                        height: "100%",
                        width: `${completionPercent}%`,
                        backgroundColor: isComplete ? "#4caf78" : "#4a9eff",
                        borderRadius: 1,
                        transition: "width 0.4s ease",
                    }}
                />
            </Box>

            <Box display="flex" flexDirection="column" gap={2}>
                {CHECKLIST_ITEMS.map((item) => (
                    <ChecklistCard
                        key={item.field}
                        item={item}
                        status={localChecklist[item.field] ?? "open"}
                        onStatusChange={(newStatus) => handleStatusChange(item.field, newStatus)}
                        saving={saving}
                    />
                ))}
            </Box>
        </Box>
    );
};

export default VendorLeadOnboardingTab;